import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import PageHeader from "../components/PageHeader";
import DemoBadge from "../components/DemoBadge";
import KPI from "../components/KPI";
import { cyberApi } from "../lib/api";
import { IconCyber, IconRiskAlert, IconSensors, IconCompliance } from "../brand/icons";
import { getImage } from "../brand/images";

const img = getImage("cyber-network-monitoring-room");

const Cybersecurity = () => {
  const [cw, setCw] = useState(null);
  useEffect(() => { cyberApi.cyberwater().then(setCw).catch(() => {}); }, []);

  return (
    <Layout>
      <SEOHead title="Cybersecurity Layer & CyberWater | AuditIQ" path="/cybersecurity"
        description="AuditIQ Cybersecurity Layer and CyberWater Security Layer defend municipal water systems, atmospheric water generation, data-center cooling, and critical operational technology." />
      <PageHeader eyebrow="Cybersecurity" demo
        title="CyberWater Security Layer"
        subtitle="A defensive-only operational-technology security module that continuously exchanges risk, event, and policy data with AuditIQ. Device identity, sensor integrity, command validity — end-to-end." />
      <section className="max-w-[1400px] mx-auto px-6 py-16">
        <div className="grid md:grid-cols-4 gap-5">
          <KPI label="Water Cyber Risk" value={cw?.water_system_cyber_risk_score ?? "—"} accent="warning" testid="cw-risk" sublabel="Lower is better" />
          <KPI label="Devices" value={cw?.device_count ?? "—"} testid="cw-devices" sublabel="Under monitoring" />
          <KPI label="Multi-Party Control" value={cw?.multi_party_control_enforced ? "ON" : "OFF"} accent="success" testid="cw-mpc" sublabel="Critical actions require dual approval" />
          <KPI label="Emergency Isolation" value={cw?.emergency_isolation_ready ? "READY" : "—"} accent="success" testid="cw-iso" sublabel="Forensic-safe quarantine" />
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 pb-16 grid lg:grid-cols-[1.2fr_0.8fr] gap-10">
        <div className="panel-elev p-0 overflow-hidden">
          <img src={img.src} alt={img.alt} className="w-full aspect-[16/9] object-cover opacity-90" />
        </div>
        <div className="grid gap-4 content-start">
          {[
            { I: IconCyber, t: "Device Identity Verification", d: "Zero-trust authentication for every PLC, sensor, pump, valve, and gateway." },
            { I: IconSensors, t: "Sensor Integrity", d: "Spoofing, tampering, and anomaly detection across pressure, flow, humidity, dew-point." },
            { I: IconRiskAlert, t: "Command Validation", d: "Every PLC/SCADA command is validated — critical actions require dual approval." },
            { I: IconCompliance, t: "AuditIQ Policy Sync", d: "Thresholds, tolerance, and intervention directives flow from AuditIQ continuously." },
          ].map(({ I, t, d }) => (
            <div key={t} className="panel p-5 flex gap-4">
              <div className="shrink-0"><I size={26} /></div>
              <div><div className="font-display text-lg text-cream">{t}</div>
                <div className="text-sm text-cream-dim mt-1">{d}</div></div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 pb-24">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-display text-2xl">Protected Devices <DemoBadge /></h3>
          <div className="text-xs text-steel">avg trust / risk across fleet</div>
        </div>
        <div className="panel-elev overflow-x-auto">
          <table className="w-full text-sm" data-testid="device-table">
            <thead className="text-[11px] uppercase tracking-[0.2em] text-steel">
              <tr className="border-b border-gold-dim/20">
                <th className="text-left p-4">Device</th>
                <th className="text-left p-4">Type</th>
                <th className="text-left p-4">Location</th>
                <th className="text-left p-4">Status</th>
                <th className="text-right p-4">Trust</th>
                <th className="text-right p-4">Risk</th>
              </tr>
            </thead>
            <tbody>
              {(cw?.devices || []).map((d) => (
                <tr key={d.id} className="border-b border-gold-dim/10 hover:bg-navy-soft/40 transition-colors">
                  <td className="p-4 font-mono text-gold">{d.name}</td>
                  <td className="p-4 text-cream-dim">{d.asset_type}</td>
                  <td className="p-4 text-cream-dim">{d.location}</td>
                  <td className="p-4"><span className={`text-xs uppercase tracking-wider ${
                    d.status === "online" ? "text-[color:var(--auditiq-success)]" :
                    d.status === "quarantined" ? "text-[color:var(--auditiq-critical)]" :
                    "text-[color:var(--auditiq-warning)]"}`}>{d.status}</span></td>
                  <td className="p-4 text-right metric text-gold">{d.trust_score}</td>
                  <td className="p-4 text-right metric text-[color:var(--auditiq-warning)]">{d.risk_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </Layout>
  );
};

export default Cybersecurity;
